/**
 * Order Summary Widget
 *
 * Displays order status summary with visual indicators.
 * Connected to live Dashboard API with mock fallback.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React from 'react';
import {
  Clock,
  PlayCircle,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import { WidgetInstance, OrderSummary } from '../../types';
import { getOrderSummary } from '../../mockDataService';
import { fetchOrderSummary } from '../../../../../../services/dashboardService';
import { useWidgetData } from '../../hooks/useWidgetData';
import WidgetWrapper from '../WidgetWrapper';
import { WidgetSkeleton } from '../shared';

interface OrderSummaryWidgetProps {
  instance: WidgetInstance;
}

const OrderSummaryWidget: React.FC<OrderSummaryWidgetProps> = ({ instance }) => {
  const { data, isLoading, error, refresh, isFirstLoad, isLive } = useWidgetData<OrderSummary>({
    widgetId: 'order-summary',
    instance,
    fetchFn: async () => {
      try {
        return await fetchOrderSummary();
      } catch {
        return getOrderSummary();
      }
    },
  });

  if (isFirstLoad) {
    return (
      <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={true}>
        <WidgetSkeleton variant="stat" count={4} />
      </WidgetWrapper>
    );
  }

  const TrendIcon = data?.trend === 'up' ? TrendingUp : data?.trend === 'down' ? TrendingDown : Minus;
  const trendColor = data?.trend === 'up' ? 'text-green-400' : data?.trend === 'down' ? 'text-red-400' : 'text-gray-400';

  return (
    <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={isLoading} error={error?.message} isLive={isLive}>
      {data && (
        <div className="h-full flex flex-col">
          {/* Main stat */}
          <div className="text-center mb-4">
            <div className="text-3xl font-bold text-white">{data.todayTotal}</div>
            <div className="text-xs text-gray-400 flex items-center justify-center gap-1">
              ordrer i dag
              <span className={`flex items-center ${trendColor}`}>
                <TrendIcon className="w-3 h-3" />
                {data.trendValue}%
              </span>
            </div>
          </div>

          {/* Status breakdown */}
          <div className="grid grid-cols-2 gap-2 flex-1">
            <div className="bg-gray-700/50 rounded-lg p-2 flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              <div>
                <div className="text-lg font-semibold text-white">{data.pending}</div>
                <div className="text-xs text-gray-400">Venter</div>
              </div>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-2 flex items-center gap-2">
              <PlayCircle className="w-5 h-5 text-blue-400" />
              <div>
                <div className="text-lg font-semibold text-white">{data.inProgress}</div>
                <div className="text-xs text-gray-400">Pagar</div>
              </div>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-2 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <div>
                <div className="text-lg font-semibold text-white">{data.completed}</div>
                <div className="text-xs text-gray-400">Fullfort</div>
              </div>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <div>
                <div className="text-lg font-semibold text-white">{data.delayed}</div>
                <div className="text-xs text-gray-400">Forsinket</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </WidgetWrapper>
  );
};

export default OrderSummaryWidget;
